'use client'

import { ArrowLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { FieldValues, FormProvider, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stepper } from '@/components/molecules/stepper'
import { Button } from '@/components/ui/button'

export type StepDef<TForm extends FieldValues> = {
  id?: string | number
  // component that renders the step fields — it receives nothing because
  // it will call useFormContext<TForm>() internally
  Component: React.ComponentType
  description?: string
  // list of field names this step is responsible for validating
  fields?: (keyof TForm | string)[]
  title: string
}

export type FormWizardProps<TForm extends FieldValues> = {
  /** Optional render for header; receives index + total + goTo */
  renderHeader?(opts: {
    isValidByStep: boolean[] | null
    goTo(i: number): void
    index: number
    total: number
  }): React.ReactNode
  formOptions?: Parameters<typeof useForm<TForm>>[0]
  initialStep?: number
  onSubmit: SubmitHandler<TForm>
  /** Override the submit button label. Defaults to t('global.submit'). */
  submitLabel?: string
  // optional resolver for Zod/Yup/etc. Use RHF Resolver type
  steps: StepDef<TForm>[]
}

/**
 * Generic FormWizard component
 * - Single RHF form instance shared across steps
 * - `next` triggers validation for the current step's `fields` (if provided)
 * - `goTo` can still be used to jump to a step; it optionally validates current step
 */
const Wizard = <TForm extends Record<string, unknown>>({
  formOptions,
  initialStep = 0,
  onSubmit,
  steps,
  submitLabel,
}: FormWizardProps<TForm>) => {
  // attach resolver to formOptions if provided
  const methods = useForm<TForm>({ reValidateMode: 'onChange', ...(formOptions || {}) })
  const { handleSubmit, trigger } = methods as UseFormReturn<TForm>
  const { t } = useTranslation()

  const [index, setIndex] = useState(initialStep)
  const total = steps.length

  const current = steps[index]

  const goTo = async (i: number, opts?: { validateCurrent?: boolean }) => {
    const validateCurrent = opts?.validateCurrent ?? true

    if (i === index) return

    if (validateCurrent && current?.fields?.length) {
      // trigger only current fields
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ok = await trigger(current.fields as any)
      if (!ok) return // don't navigate
    }

    setIndex(Math.max(0, Math.min(total - 1, i)))
  }

  const next = async () => {
    if (!current?.fields?.length) return goTo(index + 1, { validateCurrent: false })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ok = await trigger(current.fields as any)

    if (!ok) return

    if (index < total - 1) setIndex(i => i + 1)
  }

  const back = () => setIndex(i => Math.max(0, i - 1))

  const submit = handleSubmit(async (values: TForm) => {
    // optionally, you could validate all steps here by calling trigger on all fields
    try {
      await onSubmit(values)
    } catch (error) {
      methods.setError('root', { message: (error as Error).message, type: 'manual' })
    }
  })

  const ActiveStepComponent = current?.Component ?? (() => null)

  return (
    <FormProvider {...methods}>
      <form className="flex gap-8 w-full" onSubmit={submit}>
        {/* Header — either custom or default */}
        {/* {renderHeader ? (
          renderHeader({ goTo, index, isValidByStep: validCache, total })
        ) : (
          <DefaultHeader goTo={goTo} index={index} isValidByStep={validCache} steps={steps} />
        )} */}

        {/* Active step */}
        {steps.length > 1 && (
          <div className="hidden md:block">
            <Stepper
              activeIndex={index}
              onSelectStepIndex={stepIndex => goTo(stepIndex)}
              steps={steps.map((step, i) => ({
                description: step.description,
                index: i,
                isActive: i === index,
                state: i === index ? 'active' : 'default',
                title: step.title,
              }))}
            />
          </div>
        )}
        <div className="space-y-8 flex-1">
          <div className="">
            <ActiveStepComponent />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2">
            {index > 0 && (
              <Button
                disabled={index === 0}
                onClick={back}
                size="lg"
                type="button"
                variant="outline"
              >
                <ArrowLeftIcon /> {t('global.back')}
              </Button>
            )}

            <div className="flex items-center  gap-2">
              {index < total - 1 ? (
                <Button onClick={next} size="lg" type="button">
                  {t('global.next')}
                </Button>
              ) : (
                <Button
                  disabled={methods.formState.isSubmitting}
                  isLoading={methods.formState.isSubmitting}
                  onClick={submit}
                  size="lg"
                  type="button"
                >
                  {submitLabel ?? t('global.submit')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default Wizard
